import type { Theme } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { memo } from 'react';
import { StakeErrors } from '../../../../@types';
import { useBetslipContext } from '../../../../contexts/BetslipContext';
import { hasUpdateStakesAction } from '../../../../helpers';
import { OddsChangeStatus } from '../../../../helpers/betslipMessageParser';
import { useUserData } from '../../../../queries';
import useLocalization from '../../../../hooks/useLocalization';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import NeobetRaisedActionButton from './NeobetRaisedActionButton';

const styles = {
  button: {
    borderRadius: 1,
  },
  placeBetAmount: {
    color: 'neutral.25',
    fontWeight: 800,
    '.Mui-disabled &': {
      color: 'neutral.200',
    },
  },
} as const;

type Props = {
  stakeErrors: StakeErrors;
  totalStakeAmount: number;
  updateStakes: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isDisabled: boolean;
  disabledSystems: string[];
};

const NeobetPlaceBetButton = ({ stakeErrors, totalStakeAmount, updateStakes, isDisabled, disabledSystems }: Props) => {
  const { data: userSettings } = useUserData();
  const { bets, errors, clearErrors } = useBetslipContext();
  const { t } = useLocalization();

  const oddsChanges = userSettings?.sportsbookConfig?.oddsChanges ?? 'none';

  const acceptChanges = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    clearErrors();
  };

  const hasOnlySystemErrors = Object.keys(stakeErrors).length === disabledSystems.length;
  const showUpdateStakesButton = hasUpdateStakesAction(stakeErrors);

  if (!isEmpty(stakeErrors) && showUpdateStakesButton) {
    return (
      <NeobetRaisedActionButton
        disableRipple
        fullWidth
        visualVariant='form'
        sx={styles.button}
        onClick={updateStakes}
        disabled={hasOnlySystemErrors}
        label={t('updateStakes')}
      />
    );
  }

  const hasDecreasedOdds = bets.some((bet) => bet.oddsChange === OddsChangeStatus.decreased);
  if (!!errors.length && (oddsChanges === 'none' || (hasDecreasedOdds && oddsChanges === 'rising'))) {
    return (
      <NeobetRaisedActionButton
        disableRipple
        fullWidth
        visualVariant='form'
        sx={styles.button}
        onClick={acceptChanges}
        label={t('acceptChanges')}
      />
    );
  }

  return (
    <NeobetRaisedActionButton
      disableRipple
      fullWidth
      visualVariant='form'
      type='submit'
      sx={styles.button}
      disabled={isDisabled}
      label={
        <>
          {t('placeBet')} <FormattedBalance balance={totalStakeAmount} variant='h5' sx={styles.placeBetAmount} />
        </>
      }
    />
  );
};

export default memo(NeobetPlaceBetButton);
